import { AnimatePresence, motion, Variants } from "framer-motion";
import { AnimationDefinition } from "framer-motion/types/render/utils/animation";
import { useRef, useState } from "react";
import { useUnmount } from "react-use";
import { up } from "styled-breakpoints";
import styled from "styled-components";
import { fill } from "../../styles/utils";
import { Box } from "../Box";
import { Container } from "../Container";
import { Text } from "../Text";
import { IntroQuote } from "./IntroQuote";

interface IntroStepsProps {
  onAnimateIn: () => any;
  onAnimateOut: () => any;
}

const MotionWrapper = styled(motion.div)`
  ${fill("fixed")}
`;

const TextContainer = styled(Box)`
  position: absolute;
  top: calc(50% - 40px);
  left: 50%;
  width: 100%;
  text-align: center;
  transform: translate(-50%, -50%);

  ${up("xl")} {
    top: calc(50% - 64px);
  }
`;

const initialVariants: Variants = {
  initial: { opacity: 0, y: -20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.6,
      type: "tween",
      delay: 1.6,
    },
  },
  exit: { opacity: 0, transition: { duration: 1, type: "tween" } },
};

const pageVariants: Variants = {
  initial: { opacity: 0, y: -20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.6,
      type: "tween",
      delay: 0.4,
    },
  },
  exit: { opacity: 0, transition: { duration: 1, type: "tween" } },
};

export const IntroSteps: React.FC<IntroStepsProps> = ({
  onAnimateIn,
  onAnimateOut,
}) => {
  const [progress, setProgress] = useState(0);

  const savedProg = useRef(0);

  let pageTimeOut = useRef<NodeJS.Timeout | null>(null);

  const clearPageTimeout = () => {
    if (pageTimeOut.current !== null) {
      clearTimeout(pageTimeOut.current);
    }
  };

  const next = () => {
    const newProg = savedProg.current + 1;
    savedProg.current = newProg;
    setProgress(newProg);
  };

  const transition = () => {
    savedProg.current = progress;
    setProgress(-1);

    pageTimeOut.current = setTimeout(() => {
      next();
    }, 2500);
  };

  const onAnimationStart = (variant: AnimationDefinition) => {
    if (variant === "exit") {
      // setIsInTransition(true);
      onAnimateOut();
    }
    if (variant === "animate") {
      onAnimateIn();
      if (progress < 3) {
        pageTimeOut.current = setTimeout(() => {
          transition();
        }, 7000);
      }
    }
  };

  useUnmount(() => clearPageTimeout());

  return (
    <motion.div exit={{ opacity: 0 }}>
      <AnimatePresence exitBeforeEnter>
        {progress === 0 ? (
          <MotionWrapper
            key={0}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={initialVariants}
            onAnimationStart={onAnimationStart}
          >
            <TextContainer color="hotPink">
              <Box>
                <Text variant="display">In sickness</Text>
              </Box>
              <Box>
                <Text variant="display"> & in health</Text>
              </Box>
            </TextContainer>
          </MotionWrapper>
        ) : null}
        {progress === 1 ? (
          <MotionWrapper
            key={1}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            onAnimationStart={onAnimationStart}
          >
            <TextContainer>
              <Container maxWidth={2}>
                <Box color="white">
                  <Text variant="body" fontWeight={700}>
                    When the 1918 ‘flu pandemic struck, without a national
                    health service, community pharmacists provided frontline
                    healthcare. Fast-forward a hundred years, and pharmacies, as
                    essential retailers during the COVID-19 pandemic, are
                    operating high street drop-in healthcare services, COVID-19
                    testing, and vaccinations.
                  </Text>
                </Box>
              </Container>
            </TextContainer>
          </MotionWrapper>
        ) : null}
        {progress === 2 ? (
          <MotionWrapper
            key={2}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            onAnimationStart={onAnimationStart}
          >
            <TextContainer>
              <Container maxWidth={[680, null, null, null, 960]}>
                <IntroQuote />
              </Container>
            </TextContainer>
          </MotionWrapper>
        ) : null}
        {progress === 3 ? (
          <MotionWrapper
            key={1}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            onAnimationStart={onAnimationStart}
          >
            <TextContainer>
              <Container maxWidth={2}>
                <Box color="white">
                  <Text variant="body" fontWeight={700}>
                    This exhibition looks at the transformation of community
                    pharmacy into a public health service. We take a look at
                    changes in illness, mortality and the sale and efficacy of
                    medicines. We examine the impact of the National Health
                    Service, the need for new professional expertise and
                    changing public expectations. We show the enduring need for
                    health advice on the high street.
                  </Text>
                </Box>
              </Container>
            </TextContainer>
          </MotionWrapper>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
};
